import { GridColDef } from '@mui/x-data-grid-premium';
import { DraftCalculusTablePermissions, FreightCondition } from '@retail/calculus/types';
import { useCallback, useMemo } from 'react';
import { StackedTextCell } from '@retail/components';
import { PricePerUnit } from '@retail/products/components';
import { calculatePercentChange, getLLDate } from '@retail/utils';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '@retail/products/utils';
import { OrgUnitType } from '@retail/mgpris/config-types';
import { useAppTFunction } from '@retail/app/i18n';
import { DecreaseIcon, IncreaseIcon } from '@shared/custom-icons';
import { AssortmentPricesComparison } from '@retail/my-assortment/types';
import { MarkedChange } from './MarkedChange';

const baseColumnProps: Partial<GridColDef> = {
  width: 140,
  headerName: '',
  headerAlign: 'left',
  align: 'left',
};

interface Props {
  permissions: DraftCalculusTablePermissions;
  orgType: OrgUnitType;
  includeVat?: boolean;
}

export const useDraftAffectedItemsColumnDefs = ({ permissions, orgType, includeVat }: Props) => {
  const t = useAppTFunction();
  const { i18n } = useTranslation();

  const getFreightText = useCallback(
    (condition: FreightCondition) =>
      condition.freightPercentAddon
        ? `${formatPrice(condition.freightPercentAddon, i18n.language)}%`
        : t('markup.pricePerUnit', {
            price: condition.freightFixedAddOn,
            unit: condition.freightFixedAddOnUnit,
          }),
    [i18n.language, t]
  );

  const columns: { show: boolean; column: GridColDef<AssortmentPricesComparison> }[] = useMemo(
    () => [
      {
        show: true,
        column: {
          field: 'mgItemAndFinfo',
          width: 120,
          headerName: t('calculus.itemsAffected.columns.mgItemAndFinfo'),
          renderCell: ({ row }) => (
            <StackedTextCell topLine={row.draft.mgItemNumber} bottomLine={row.draft.finfoNumber} />
          ),
        },
      },
      {
        show: true,
        column: {
          field: 'primaryText',
          width: 240,
          headerName: t('calculus.itemsAffected.columns.itemName'),
          renderCell: ({ row }) => {
            return (
              <StackedTextCell
                topLine={row.draft.primaryText}
                bottomLine={row.draft.secondaryText}
                nullableBottomLine
              />
            );
          },
        },
      },
      {
        show: true,
        column: {
          field: 'date',
          width: 150,
          headerName: t('calculus.activeFrom'),
          renderCell: ({ row }) => getLLDate(row.draft.priority.validFrom),
        },
      },
      {
        show: permissions.purchaseAgreements.includes('READ'),
        column: {
          ...baseColumnProps,
          field: 'purchasePrice',
          headerName: t('calculus.prices.purchasePrice'),
          renderCell: ({ row }) => {
            const currentPurchasePrice = row.base.pricesExVat.purchasePrice;
            const newPurchasePrice = row.draft.pricesExVat.purchasePrice;

            const primaryUnit = row.draft.primaryUnit;

            if (!currentPurchasePrice && !newPurchasePrice) return '-';

            return newPurchasePrice === currentPurchasePrice ? (
              <PricePerUnit price={currentPurchasePrice ?? '-'} unit={primaryUnit} />
            ) : (
              <StackedTextCell
                centerAlign
                topLine={
                  <MarkedChange>
                    <PricePerUnit price={newPurchasePrice ?? '-'} unit={primaryUnit} />
                  </MarkedChange>
                }
                bottomLine={<PricePerUnit price={currentPurchasePrice ?? '-'} unit={primaryUnit} />}
              />
            );
          },
        },
      },
      {
        show: permissions.purchaseAgreements.includes('READ'),
        column: {
          ...baseColumnProps,
          field: 'purchasePriceDifference',
          headerName: t('calculus.itemsAffected.columns.changes'),
          renderCell: ({ row }) => {
            const currentPurchasePrice = row.base.pricesExVat.purchasePrice;
            const newPurchasePrice = row.draft.pricesExVat.purchasePrice;

            if (!currentPurchasePrice || !newPurchasePrice) return '-';

            const percentageChange = calculatePercentChange(currentPurchasePrice, newPurchasePrice);
            return (
              <Stack flexDirection="row" alignItems="center" gap={1}>
                {percentageChange > 0 ? (
                  <IncreaseIcon color="warning" fontSize="small" />
                ) : (
                  percentageChange > 0 && <DecreaseIcon color="success" fontSize="small" />
                )}
                {formatPrice(percentageChange, i18n.language) + '%'}
              </Stack>
            );
          },
        },
      },
      {
        show: permissions.freight.includes('READ'),
        column: {
          ...baseColumnProps,
          field: 'freight',
          width: 80,
          headerName: t('calculus.itemsAffected.columns.freight'),
          renderCell: ({ row }) => {
            const newFreightValue = row.base.freightCondition
              ? getFreightText(row.base.freightCondition)
              : undefined;

            const currentFreightValue = row.draft.freightCondition
              ? getFreightText(row.draft.freightCondition)
              : undefined;

            return currentFreightValue === newFreightValue ? (
              currentFreightValue
            ) : (
              <StackedTextCell
                centerAlign
                nullableBottomLine
                topLine={<MarkedChange>{newFreightValue}</MarkedChange>}
                bottomLine={currentFreightValue}
                slotProps={{ bottomLine: { lineThrough: true } }}
              />
            );
          },
        },
      },
      {
        show:
          permissions.purchaseAgreements.includes('READ') && permissions.freight.includes('READ'),
        column: {
          field: 'costPrice',
          ...baseColumnProps,
          headerName: t('calculus.prices.costPrice'),
          renderCell: ({ row }) => {
            const currentCostPrice = row.base.pricesExVat.costPrice;
            const newCostPrice = row.draft.pricesExVat.costPrice;

            const primaryUnit = row.draft.primaryUnit;

            if (!currentCostPrice && !newCostPrice) return '-';

            return currentCostPrice === newCostPrice ? (
              <PricePerUnit price={primaryUnit ? currentCostPrice ?? '-' : ''} unit={primaryUnit} />
            ) : (
              <StackedTextCell
                centerAlign
                topLine={
                  <PricePerUnit price={primaryUnit ? newCostPrice ?? '-' : ''} unit={primaryUnit} />
                }
                bottomLine={
                  <PricePerUnit
                    price={primaryUnit ? currentCostPrice ?? '-' : ''}
                    unit={primaryUnit}
                  />
                }
                slotProps={{ bottomLine: { lineThrough: true } }}
              />
            );
          },
        },
      },
      {
        show:
          permissions.purchaseAgreements.includes('READ') && permissions.freight.includes('READ'),
        column: {
          field: 'costPricePriceDifference',
          ...baseColumnProps,
          headerName: t('calculus.itemsAffected.columns.changes'),
          renderCell: ({ row }) => {
            const currentCostPrice = row.base.pricesExVat.costPrice;
            const newCostPrice = row.draft.pricesExVat.costPrice;

            if (!currentCostPrice || !newCostPrice) return '-';

            const percentageChange = calculatePercentChange(currentCostPrice, newCostPrice);
            return (
              <Stack flexDirection="row" alignItems="center" gap={1}>
                {percentageChange > 0 ? (
                  <IncreaseIcon color="warning" fontSize="small" />
                ) : (
                  percentageChange > 0 && <DecreaseIcon color="success" fontSize="small" />
                )}
                {formatPrice(percentageChange, i18n.language) + '%'}
              </Stack>
            );
          },
        },
      },
      {
        show: orgType !== OrgUnitType.WHOLESALE_PRICE_LIST,
        column: {
          ...baseColumnProps,
          field: 'origin',
          headerName: t('myAssortment.columns.originatesFrom'),
          width: 100,
          renderCell: ({ row }) => {
            const currentMarkupOrPriceSuggestion = row.base.markupOrPriceSuggestionCondition;
            const newMarkupOrPriceSuggestion = row.draft.markupOrPriceSuggestionCondition;
            const currentOrigin =
              currentMarkupOrPriceSuggestion?.type === 'MARKUP'
                ? t('myAssortment.markup')
                : t('calculus.itemsAffected.columns.priceSuggestion');
            const newOrigin =
              newMarkupOrPriceSuggestion?.type === 'MARKUP'
                ? t('myAssortment.markup')
                : t('calculus.itemsAffected.columns.priceSuggestion');

            if (!currentOrigin && !newOrigin) return '-';

            return newOrigin === currentOrigin ? (
              currentOrigin
            ) : (
              <StackedTextCell
                topLine={newOrigin}
                bottomLine={currentOrigin}
                slotProps={{ bottomLine: { lineThrough: true } }}
              />
            );
          },
        },
      },
      {
        show: orgType !== OrgUnitType.WHOLESALE_PRICE_LIST,
        column: {
          field: 'salesCoverage',
          ...baseColumnProps,
          headerName: t('calculus.coverageDegree'),
          renderCell: ({ row }) => {
            const currentCoverageDegree = row.base.markupOrPriceSuggestionCondition?.coverageDegree;
            const newCoverageDegree = row.draft.markupOrPriceSuggestionCondition?.coverageDegree;

            if (!currentCoverageDegree || !newCoverageDegree) return '-';

            return currentCoverageDegree === newCoverageDegree ? (
              currentCoverageDegree + '%'
            ) : (
              <StackedTextCell
                centerAlign
                topLine={<MarkedChange>{newCoverageDegree + '%'}</MarkedChange>}
                bottomLine={currentCoverageDegree + '%'}
                slotProps={{ bottomLine: { lineThrough: true } }}
              />
            );
          },
        },
      },
      {
        show:
          permissions.purchaseAgreements.includes('READ') &&
          permissions.freight.includes('READ') &&
          permissions.markup.includes('READ'),
        column: {
          field: 'salesPrice',
          ...baseColumnProps,
          headerName: t('calculus.prices.salesPrice'),
          renderCell: ({ row }) => {
            const currentSalesPrice = includeVat
              ? row.base.pricesIncVat.salesPrice
              : row.base.pricesExVat.salesPrice;
            const newSalesPrice = includeVat
              ? row.draft.pricesIncVat.salesPrice
              : row.draft.pricesExVat.salesPrice;

            const primaryUnit = row.draft.primaryUnit;

            if (!currentSalesPrice && !newSalesPrice) return '-';

            return currentSalesPrice === newSalesPrice ? (
              <PricePerUnit price={currentSalesPrice ?? '-'} unit={primaryUnit} />
            ) : (
              <StackedTextCell
                centerAlign
                topLine={<PricePerUnit price={newSalesPrice ?? '-'} unit={primaryUnit} />}
                bottomLine={<PricePerUnit price={currentSalesPrice ?? '-'} unit={primaryUnit} />}
                slotProps={{ bottomLine: { lineThrough: true } }}
              />
            );
          },
        },
      },
      {
        show:
          permissions.purchaseAgreements.includes('READ') &&
          permissions.freight.includes('READ') &&
          permissions.markup.includes('READ'),
        column: {
          field: 'salesPricePriceDifference',
          ...baseColumnProps,
          headerName: t('calculus.itemsAffected.columns.changes'),
          renderCell: ({ row }) => {
            const currentSalesPrice = row.base.pricesExVat.salesPrice;
            const newSalesPrice = row.draft.pricesExVat.salesPrice;

            if (!currentSalesPrice || !newSalesPrice) return '-';

            const percentageChange = calculatePercentChange(currentSalesPrice, newSalesPrice);
            return (
              <Stack flexDirection="row" alignItems="center" gap={1}>
                {percentageChange > 0 ? (
                  <IncreaseIcon color="warning" fontSize="small" />
                ) : (
                  percentageChange > 0 && <DecreaseIcon color="success" fontSize="small" />
                )}
                {formatPrice(percentageChange, i18n.language) + '%'}
              </Stack>
            );
          },
        },
      },
    ],
    [
      t,
      permissions.purchaseAgreements,
      permissions.freight,
      permissions.markup,
      orgType,
      includeVat,
      i18n.language,
      getFreightText,
    ]
  );

  return columns;
};
